/**
 * Blossom — variant-picker.js
 *
 * <product-info>   Owns a product's state on the PDP and in quick add. Reads the
 *                  JSON rendered by snippets/product-json.liquid (pre-formatted,
 *                  Markets-safe prices — money is never formatted in JS) and keeps
 *                  every dependent element in sync with the selected variant and
 *                  purchase option:
 *                    form[data-product-form] input[name="id"|"selling_plan"]
 *                    [data-product-price] · [data-add-button] [data-add-label]
 *                    [data-product-sku] · [data-product-inventory]
 *                    [data-selling-plan-price] · [data-one-time-price]
 *                    [data-selected-variant-title] · [data-product-url-link]
 *                  Emits theme:product:variant-change { variant, sectionId, productId }.
 *
 * <variant-picker> Option radios / selects rendered by snippets/variant-picker.liquid.
 *                  Tracks selected values, per-value availability (slash + text,
 *                  never color alone) and the shade-family filter tabs.
 */
(() => {
  if (!window.Theme) return;

  const escapeHTML = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  /* ------------------------------------------------------ <product-info> */
  if (!customElements.get('product-info')) {
    class ProductInfo extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        const json = this.querySelector('script[data-product-json]');
        if (!json) return;
        try {
          this.data = JSON.parse(json.textContent);
        } catch (error) {
          console.error('[product-info] Invalid product JSON', error);
          return;
        }
        this.initialized = true;
        this.sectionId = this.dataset.sectionId;
        this.strings = this.data.strings || {};
        this.variants = this.data.variants || [];
        const currentId = Number(this.querySelector('form[data-product-form] input[name="id"]')?.value);
        this.currentVariant = this.variants.find((variant) => variant.id === currentId) || this.variants[0] || null;

        this.addEventListener('variant-picker:change', (event) => this.onOptionsChange(event.detail.options));
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-selling-plan-option]')) this.onSellingPlanChange(event.target.value);
        });
      }

      get sellingPlanInputs() {
        return this.querySelectorAll('form[data-product-form] input[name="selling_plan"]');
      }

      get selectedSellingPlan() {
        const input = this.sellingPlanInputs[0];
        return input ? input.value : '';
      }

      findVariant(options) {
        return this.variants.find((variant) => variant.options.every((value, index) => value === options[index])) || null;
      }

      onOptionsChange(options) {
        const variant = this.findVariant(options);
        this.currentVariant = variant;
        this.picker?.updateAvailability(options, this.variants, this.strings);
        this.render(variant);
        if (variant && this.dataset.updateUrl === 'true') this.updateURL(variant);
        Theme.emit(
          Theme.events.VARIANT_CHANGE,
          { variant, sectionId: this.sectionId, productId: this.data.id },
          this
        );
        Theme.announce(this.describe(variant));
      }

      onSellingPlanChange(planId) {
        this.sellingPlanInputs.forEach((input) => {
          input.value = planId;
        });
        this.renderButtons(this.currentVariant);
      }

      get picker() {
        return this.querySelector('variant-picker');
      }

      render(variant) {
        this.querySelectorAll('form[data-product-form] input[name="id"]').forEach((input) => {
          input.value = variant ? variant.id : '';
          input.disabled = !variant;
        });
        this.renderPrice(variant);
        this.renderButtons(variant);
        this.renderSku(variant);
        this.renderInventory(variant);
        this.renderSellingPlans(variant);
        this.querySelectorAll('[data-selected-variant-title]').forEach((el) => {
          el.textContent = variant ? variant.title : this.strings.unavailable;
        });
        this.querySelectorAll('[data-product-url-link]').forEach((link) => {
          if (!variant) return;
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('variant', variant.id);
          link.href = url.pathname + url.search;
        });
      }

      priceMarkup(variant, extraClass = '') {
        const s = this.strings;
        const classes = ['price', extraClass, variant.compare_at_price ? 'price--on-sale' : ''].filter(Boolean).join(' ');
        let html = `<div class="${classes}" data-price>`;
        if (variant.compare_at_price) {
          html += `<span class="visually-hidden">${escapeHTML(s.salePrice)}</span>`;
          html += `<span class="price__sale">${variant.price}</span>`;
          html += `<span class="visually-hidden">${escapeHTML(s.regularPrice)}</span>`;
          html += `<s class="price__compare">${variant.compare_at_price}</s>`;
        } else {
          html += `<span class="price__regular">${variant.price}</span>`;
        }
        if (variant.unit_price) {
          html += `<span class="price__unit"><span class="visually-hidden">${escapeHTML(s.unitPrice)}</span>${variant.unit_price}</span>`;
        }
        return `${html}</div>`;
      }

      renderPrice(variant) {
        this.querySelectorAll('[data-product-price]').forEach((wrapper) => {
          if (!variant) {
            wrapper.style.visibility = 'hidden';
            return;
          }
          wrapper.style.visibility = '';
          wrapper.innerHTML = this.priceMarkup(variant, wrapper.dataset.priceClass || '');
        });
      }

      renderButtons(variant) {
        const s = this.strings;
        const comingSoon = this.dataset.comingSoon === 'true';
        const plan = this.selectedSellingPlan;
        const planPrice = variant && plan && variant.selling_plans ? variant.selling_plans[plan] : null;
        const price = planPrice ? planPrice.price : variant?.price;

        this.querySelectorAll('[data-add-button]').forEach((button) => {
          const label = button.querySelector('[data-add-label]');
          let text;
          let enabled = false;
          if (comingSoon) text = escapeHTML(s.comingSoon);
          else if (!variant) text = escapeHTML(s.unavailable);
          else if (!variant.available) text = escapeHTML(s.soldOut);
          else {
            enabled = true;
            text = `${escapeHTML(s.addToCart)}<span class="product-form__separator" aria-hidden="true"> – </span><span class="product-form__price">${price}</span>`;
          }
          if (label) label.innerHTML = text;
          if (enabled) {
            button.removeAttribute('disabled');
            delete button.dataset.soldOut;
          } else {
            button.setAttribute('disabled', '');
            button.dataset.soldOut = 'true';
          }
        });
      }

      renderSku(variant) {
        this.querySelectorAll('[data-product-sku]').forEach((el) => {
          const value = el.querySelector('[data-sku-value]');
          const sku = variant?.sku || '';
          if (value) value.textContent = sku;
          el.hidden = !sku;
        });
      }

      renderInventory(variant) {
        this.querySelectorAll('[data-product-inventory]').forEach((el) => {
          const text = el.querySelector('[data-inventory-text]');
          let message = '';
          let state = '';
          if (variant && variant.available) {
            if (variant.low_stock) {
              message = (this.strings.lowStock || '').replace('[count]', variant.low_stock);
              state = 'low';
            } else if (el.dataset.showInStock === 'true') {
              message = this.strings.inStock;
              state = 'in-stock';
            }
          }
          if (text) text.textContent = message;
          el.dataset.state = state;
          el.hidden = !message;
        });
      }

      renderSellingPlans(variant) {
        if (!variant) return;
        this.querySelectorAll('[data-one-time-price]').forEach((el) => {
          el.innerHTML = variant.price;
        });
        this.querySelectorAll('[data-selling-plan-price]').forEach((el) => {
          const plan = variant.selling_plans?.[el.dataset.sellingPlanPrice];
          if (!plan) {
            el.textContent = '';
            return;
          }
          el.innerHTML = plan.compare_at_price
            ? `${plan.price} <s class="price__compare">${plan.compare_at_price}</s>`
            : plan.price;
        });
      }

      updateURL(variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }

      describe(variant) {
        if (!variant) return this.strings.unavailable;
        const status = variant.available ? '' : `, ${this.strings.soldOut}`;
        return `${variant.title}${status}`;
      }
    }

    customElements.define('product-info', ProductInfo);
  }

  /* ---------------------------------------------------- <variant-picker> */
  if (!customElements.get('variant-picker')) {
    class VariantPicker extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.addEventListener('change', (event) => {
          if (!event.target.matches('[data-option-value], [data-option-select]')) return;
          this.updateSelectedLabels();
          this.dispatchEvent(
            new CustomEvent('variant-picker:change', { detail: { options: this.selectedOptions }, bubbles: true })
          );
        });
        this.addEventListener('click', (event) => {
          const filter = event.target.closest('[data-shade-filter]');
          if (filter) this.applyShadeFilter(filter);
        });
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll('[data-option-position]'));
      }

      get selectedOptions() {
        return this.optionGroups.map((group) => {
          const select = group.querySelector('[data-option-select]');
          if (select) return select.value;
          const checked = group.querySelector('[data-option-value]:checked');
          return checked ? checked.value : null;
        });
      }

      updateSelectedLabels() {
        this.optionGroups.forEach((group, index) => {
          const label = group.querySelector('[data-selected-value]');
          if (label) label.textContent = this.selectedOptions[index] || '';
        });
      }

      /**
       * A value is available when a variant with that value — combined with the
       * other currently selected values — exists and is in stock.
       */
      updateAvailability(selected, variants, strings) {
        this.optionGroups.forEach((group, position) => {
          const check = (value) => {
            const matches = variants.filter(
              (variant) =>
                variant.options[position] === value &&
                variant.options.every((optionValue, index) => index === position || optionValue === selected[index])
            );
            if (!matches.length) return 'unavailable';
            return matches.some((variant) => variant.available) ? 'available' : 'sold-out';
          };

          group.querySelectorAll('[data-option-value]').forEach((input) => {
            const state = check(input.value);
            const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            if (!label) return;
            label.classList.toggle('is-unavailable', state !== 'available');
            const text = label.querySelector('[data-unavailable-text]');
            if (text) {
              text.textContent =
                state === 'sold-out' ? `, ${strings.optionSoldOut}` : state === 'unavailable' ? `, ${strings.optionUnavailable}` : '';
            }
          });

          group.querySelectorAll('[data-option-select] option').forEach((option) => {
            const state = check(option.value);
            const base = option.dataset.label || option.value;
            option.textContent =
              state === 'sold-out'
                ? `${base} – ${strings.optionSoldOut}`
                : state === 'unavailable'
                  ? `${base} – ${strings.optionUnavailable}`
                  : base;
          });
        });

        const description = this.querySelector('[data-shade-description]');
        if (description) {
          const variant = variants.find((item) => item.options.every((value, index) => value === selected[index]));
          const text = variant?.shade_description || '';
          description.textContent = text;
          description.hidden = !text;
        }
      }

      applyShadeFilter(button) {
        const family = button.dataset.shadeFilter;
        const group = button.closest('[data-option-position]');
        if (!group) return;
        group.querySelectorAll('[data-shade-filter]').forEach((tab) => {
          tab.setAttribute('aria-pressed', String(tab === button));
        });
        group.querySelectorAll('label[data-shade-family]').forEach((label) => {
          const input = group.querySelector(`#${CSS.escape(label.htmlFor)}`);
          const visible = !family || label.dataset.shadeFamily === family || (input && input.checked);
          label.hidden = !visible;
          if (input) input.hidden = !visible;
        });
      }
    }

    customElements.define('variant-picker', VariantPicker);
  }
})();
